/**
 * ActorNode.tsx — a custom React Flow node representing an actor or gate node
 * (supplier, kitchen, CRM, ...).
 *
 * Renders: icon + label (always), the current headline stock (the balance-
 * sheet quantity for this node, from the latest tick), and an in/out badge
 * showing this tick's flow. The node's stroke colour reflects its group;
 * Phase 4 overlays bottleneck/anomaly/cluster styling here.
 *
 * Clicking the node opens the nerd-mode NodeModal. Right-clicking opens the
 * context menu (Edit / Duplicate / Delete).
 *
 * Icon rendering: if `icon` matches a Phosphor name in `lookupIcon`, the SVG
 * renders; otherwise the raw string renders (the emoji-backcompat path for
 * ghost_kitchen scenarios that ship emoji icons).
 *
 * Drag affordance (#2): the `dragging` prop from React Flow drives a
 * `.is-dragging` class — grab cursor + lift shadow.
 */
import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';

import { formatNum } from './format';
import { lookupIcon } from './icons';

/**
 * Multi-snap-point handles per side (#multi-snap).
 *
 * Each side renders a CENTER handle (the visible dot — the historical anchor)
 * plus two INVISIBLE snap points offset toward the corners (at ~25% and ~75%
 * of the side's length). When the user draws or reconnects an edge, they can
 * land on any of the three; the chosen handle id encodes which one, so the
 * edge stays anchored exactly where it was dropped.
 *
 * Id scheme: `<type>-<side>` (center), `<type>-<side>-t` (top-ish quarter),
 * `<type>-<side>-b` (bottom-ish quarter). On Top/Bottom the suffixes are `-l`
 * (left quarter) and `-r` (right quarter).
 *
 * React Flow positions a handle by its `position` (which side) + an optional
 * transform via `style`. The quarter offsets are relative to the node box, so
 * `top: 25%` on a Left handle moves it up by a quarter of the node's height.
 * Width/height 0 + opacity 0 + pointer-events all keeps the snap points
 * invisible-but-droppable without disturbing the center dot.
 */
type Side = 'left' | 'top' | 'right' | 'bottom';
const SIDES: Side[] = ['left', 'top', 'right', 'bottom'];
const POSITION: Record<Side, Position> = {
  left: Position.Left,
  top: Position.Top,
  right: Position.Right,
  bottom: Position.Bottom,
};

/** Quarter offsets for the two invisible snap points on a given side. */
function snapOffsets(side: Side): Array<{ suffix: string; style: React.CSSProperties }> {
  // Horizontal sides (top/bottom) shift left↔right; vertical sides shift up↕down.
  if (side === 'top' || side === 'bottom') {
    return [
      { suffix: 'l', style: { left: '25%', transform: 'translate(-50%, 0)' } },
      { suffix: 'r', style: { left: '75%', transform: 'translate(-50%, 0)' } },
    ];
  }
  return [
    { suffix: 't', style: { top: '25%', transform: 'translate(0, -50%)' } },
    { suffix: 'b', style: { top: '75%', transform: 'translate(0, -50%)' } },
  ];
}

/** Render the three handles (1 center visible + 2 invisible snap points) for
 *  one side, for both source and target handle types. Loose mode means every
 *  handle accepts either direction, so we render both kinds at each point.
 *
 * React Flow reads each handle's position from its DOM bounding rect, so the
 * quarter offsets are applied via the Handle's own `style` (transform) — not a
 * wrapper element, which RF wouldn't measure. */
function SideHandles({ side }: { side: Side }): JSX.Element {
  const pos = POSITION[side];
  const offsets = snapOffsets(side);
  return (
    <>
      {/* Center — the visible dot. */}
      <Handle type="target" position={pos} id={`t-${side}`} className="actor-handle" />
      <Handle type="source" position={pos} id={`s-${side}`} className="actor-handle" />
      {/* Invisible snap points at the quarter positions. Opacity 0 + 0 size so
          they never render visually; pointer-events stays 'all' so they catch
          the drop. The transform offsets them along the side from center. */}
      {offsets.map(({ suffix, style }) => (
        // Fragment can't carry a key, so emit a thin wrapper keyed by slot —
        // the handles inside carry their own ids.
        <HandleFragment key={`${side}-${suffix}`}>
          <Handle type="target" position={pos} id={`t-${side}-${suffix}`} className="actor-handle snap-handle" style={style} />
          <Handle type="source" position={pos} id={`s-${side}-${suffix}`} className="actor-handle snap-handle" style={style} />
        </HandleFragment>
      ))}
    </>
  );
}

/** Tiny helper because <></> can't take a key. */
function HandleFragment({ children }: { children: React.ReactNode }): JSX.Element {
  return <>{children}</>;
}

export interface ActorNodeData {
  label: string;
  icon: string;
  group?: string;
  /** Current stock — the default headline quantity. */
  stock?: number;
  stockUnit?: string;
  /** This tick's flow, for the in/out badge. */
  inflow?: number;
  outflow?: number;
  /** Cosmetic (GraphNode.displayValue): which quantity renders as the
   *  headline. Absent → 'stock'. 'none' hides headline + badge. */
  displayValue?: import('../engine/types').NodeDisplayValue | null;
  /** The resolved `state:<key>` scalar for the headline (kept scalar so the
   *  canvas's per-tick identity check stays stable). */
  stateValue?: number;
  /** Phase 4: visual cues from annotations. */
  bottleneck?: boolean;
  anomalous?: boolean;
  /** True if this node has an agentConfig attached. */
  hasAgent?: boolean;
  /** True if a bundle-level workflowTrigger sources this node (the trigger
   *  contract) — the ⚡ badge. */
  hasWorkflowTrigger?: boolean;
  /** All of this node's triggers are disarmed — the ⚡ renders dimmed. */
  triggerDisarmed?: boolean;
  /** A trigger on this node fired (or failed) seconds ago — the ⚡ pulses. */
  triggerFlash?: boolean;
  /** Click handler — selects the node (highlight + sidebar). Does NOT open
   *  the modal; that is reserved for double-click + the context menu. */
  onSelect?: (nodeId: string) => void;
  /** Double-click handler — opens the node modal. */
  onOpen?: (nodeId: string) => void;
  /** Right-click handler — opens the context menu. */
  onContext?: (nodeId: string, x: number, y: number) => void;
  /** Group → stroke colour, supplied by the canvas. */
  groupColor?: string;
  // React Flow's Node<T> requires T extends Record<string, unknown>; an index
  // signature satisfies it without weakening the typed fields above.
  [key: string]: unknown;
}

const ActorNodeBase = ({
  id,
  data,
  selected,
  dragging,
}: NodeProps & { data?: ActorNodeData }) => {
  const d = data ?? ({} as ActorNodeData);
  // Headline resolution — see the render comment below. `showBadge` turns
  // off when the badge would duplicate the headline ('inflow'/'outflow')
  // or when the user hid values ('none').
  const dv = d.displayValue ?? 'stock';
  let headline: number | undefined;
  let qualifier = '';
  let showBadge = true;
  if (dv === 'stock') headline = d.stock;
  else if (dv === 'inflow') { headline = d.inflow; qualifier = '▲'; showBadge = false; }
  else if (dv === 'outflow') { headline = d.outflow; qualifier = '▼'; showBadge = false; }
  else if (dv.startsWith('state:')) { headline = d.stateValue; qualifier = dv.slice('state:'.length); }
  else showBadge = false; // 'none'
  const ring = d.bottleneck ? '#c44e52' : d.anomalous ? '#edaf3c' : (d.groupColor ?? 'var(--neutral)');
  const iconEntry = lookupIcon(d.icon);

  return (
    <button
      type="button"
      className={`actor-node${d.bottleneck ? ' is-bottleneck' : ''}${d.anomalous ? ' is-anomaly' : ''}${dragging ? ' is-dragging' : ''}`}
      style={{
        borderColor: selected ? 'var(--ink)' : ring,
        // Phase 4 bottleneck cue: shrink the node so the chokepoint is visible
        // at a glance. CSS transition animates the change.
        transform: d.bottleneck ? 'scale(0.86)' : 'scale(1)',
      }}
      onClick={() => d.onSelect?.(id)}
      onDoubleClick={(e) => {
        e.stopPropagation();
        d.onOpen?.(id);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        d.onContext?.(id, e.clientX, e.clientY);
      }}
    >
      {/* Multi-snap-point handles: each side has a visible center dot + two
          invisible quarter snap points. Loose mode + both source/target at
          each position means you can drop a connection anywhere. */}
      {SIDES.map((side) => (
        <SideHandles key={side} side={side} />
      ))}
      <div className="actor-node-icon">
        {iconEntry ? <iconEntry.Component size={22} weight="regular" /> : d.icon}
        {d.hasAgent && <span className="agent-indicator" title="API/Agent configured" style={{ position: 'absolute', top: -4, right: -4, fontSize: 12 }}>🔌</span>}
        {d.hasWorkflowTrigger && (
          <span
            className={`agent-indicator${d.triggerFlash ? ' wf-trigger-flash' : ''}`}
            title={d.triggerFlash
              ? 'Trigger firing…'
              : d.triggerDisarmed
                ? 'Workflow trigger (disarmed)'
                : 'Triggers a workflow'}
            style={{
              position: 'absolute', top: -4, left: -4, fontSize: 12,
              opacity: d.triggerDisarmed && !d.triggerFlash ? 0.35 : 1,
            }}
          >
            ⚡
          </span>
        )}
      </div>
      <div className="actor-node-label">{d.label}</div>
      {/* Headline quantity — GraphNode.displayValue decides which per-tick
          number this is (default stock). 'inflow'/'outflow' carry the ▲/▼
          glyph and drop the badge (it would duplicate the headline);
          'state:<key>' prefixes the key; 'none' hides everything. An
          unresolvable quantity renders nothing — never a misleading 0. */}
      {headline !== undefined && (
        <div className="actor-node-stock">
          {qualifier && <span className="actor-node-qualifier">{qualifier} </span>}
          {formatNum(headline)}
          {d.stockUnit ? <span className="unit"> {d.stockUnit}</span> : null}
        </div>
      )}
      {showBadge && (d.inflow !== undefined || d.outflow !== undefined) && (
        <div className="actor-node-flow">
          <span className="in" title="inflow">
            ▲{formatNum(d.inflow ?? 0)}
          </span>
          <span className="out" title="outflow">
            ▼{formatNum(d.outflow ?? 0)}
          </span>
        </div>
      )}
    </button>
  );
};

export const ActorNode = memo(ActorNodeBase);
